import { ref } from 'vue'

type QueueItem = {
  target: string
  action: () => Promise<void> | void
}

const queue = ref<QueueItem[]>([])
const running = ref<Record<string, boolean>>({})
const paused = ref<Record<string, boolean>>({})

export function useQueue() {
  function addToQueue(target: string, action: QueueItem['action']) {
    queue.value.push({ target, action })
    // Auto-start if not paused/running
    if (!paused.value[target] && !running.value[target]) {
      void run(target)
    }
  }

  function activateQueue(target: string) {
    paused.value[target] = false
    if (!running.value[target]) {
      void run(target)
    }
  }

  /**
   * Pause processing for this target.
   * - If a task is currently running, it will finish, then pause.
   * - Optionally clear any pending tasks for this target.
   */
  function deactivateQueue(target: string, opts?: { clearPending?: boolean }) {
    paused.value[target] = true
    if (opts?.clearPending) {
      queue.value = queue.value.filter(q => q.target !== target)
    }
  }

  function clearQueue(target: string) {
    queue.value = queue.value.filter(q => q.target !== target)
  }

  function isActive(target: string) {
    return !!running.value[target] && !paused.value[target]
  }

  async function run(target: string) {
    if (running.value[target] || paused.value[target]) return
    running.value[target] = true

    try {
      while (!paused.value[target]) {
        const idx = queue.value.findIndex(q => q.target === target)
        if (idx === -1) break

        const { action } = queue.value.splice(idx, 1)[0]

        try {
          await action()
        } catch (err) {
          console.error(`Error in queued action for "${target}":`, err)
          // continue to next task
        }
      }
    } finally {
      running.value[target] = false
    }
  }

  return {
    addToQueue,
    activateQueue, // resume & run queued tasks for target
    deactivateQueue, // pause; optionally clear pending tasks
    clearQueue, // remove all pending tasks for target
    isActive
  }
}
